import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { Device } from "@twilio/voice-sdk";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TwilioVoiceContext = createContext(null);

export function TwilioVoiceProvider({ children }) {
  const { user } = useAuth();
  const deviceRef = useRef(null);
  const [deviceReady, setDeviceReady] = useState(false);
  const [currentCall, setCurrentCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [muted, setMuted] = useState(false);
  const [onHold, setOnHold] = useState(false);
  const [fromNumber, setFromNumber] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | connecting | ringing | in-call | error
  const [error, setError] = useState(null);
  const [lastCall, setLastCall] = useState(null); // {phone, contactId, durationSec, direction, at}
  const callMetaRef = useRef({}); // {phone, contactId, direction, startedAt}

  const [outOfMinutes, setOutOfMinutes] = useState(false);

  // Sales reps, company owners and super admins can use the softphone
  const enabled = user?.role === "sales_rep" || user?.role === "company_owner" || user?.role === "super_admin";

  const fetchToken = useCallback(async () => {
    const { data } = await api.get("/twilio/token");
    return data;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let device;
    let cancelled = false;

    (async () => {
      try {
        const { token, from_number } = await fetchToken();
        if (cancelled) return;
        setFromNumber(from_number);
        setOutOfMinutes(false);

        device = new Device(token, {
          logLevel: 1,
          codecPreferences: ["opus", "pcmu"],
          edge: "ashburn",
        });
        deviceRef.current = device;

        device.on("registered", () => setDeviceReady(true));
        device.on("unregistered", () => setDeviceReady(false));
        device.on("error", (e) => {
          console.error("[Twilio] error", e);
          setError(e.message || "Device error");
        });

        device.on("incoming", (call) => {
          setIncomingCall(call);
          setStatus("ringing");
          call.on("disconnect", () => {
            setIncomingCall(null);
            setCurrentCall(null);
            setMuted(false);
            setStatus("idle");
          });
          call.on("cancel", () => {
            setIncomingCall(null);
            setStatus("idle");
          });
          call.on("reject", () => {
            setIncomingCall(null);
            setStatus("idle");
          });
        });

        device.on("tokenWillExpire", async () => {
          try {
            const { token: newToken } = await fetchToken();
            device.updateToken(newToken);
          } catch (e) { console.error("token refresh failed", e); }
        });

        await device.register();
      } catch (e) {
        console.error("[Twilio] init failed", e);
        if (e?.response?.status === 402) {
          setOutOfMinutes(true);
          setError("out_of_minutes");
        } else {
          setError(e.message || "Init failed");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch {}
        deviceRef.current = null;
      }
      setDeviceReady(false);
    };
  }, [enabled, fetchToken]);

  const startCall = useCallback(async (to, extra = {}) => {
    if (!deviceRef.current || !deviceReady) {
      setError("Softphone not ready");
      return null;
    }
    setError(null);
    setStatus("connecting");
    callMetaRef.current = {
      phone: to,
      contactId: extra.ContactId || extra.contactId || null,
      direction: "outbound",
      startedAt: null,
    };
    try {
      const call = await deviceRef.current.connect({
        params: { To: to, ...extra },
      });
      call.on("accept", () => {
        callMetaRef.current.startedAt = Date.now();
        setCurrentCall(call);
        setStatus("in-call");
      });
      call.on("disconnect", () => {
        const meta = callMetaRef.current;
        const durationSec = meta.startedAt ? Math.round((Date.now() - meta.startedAt) / 1000) : 0;
        // Only show post-call modal if call actually connected (>= 3s)
        if (durationSec >= 3) {
          setLastCall({
            phone: meta.phone,
            contactId: meta.contactId,
            direction: meta.direction,
            durationSec,
            at: new Date().toISOString(),
          });
        }
        callMetaRef.current = {};
        setCurrentCall(null);
        setMuted(false);
        setOnHold(false);
        setStatus("idle");
      });
      call.on("error", (e) => {
        console.error("[Twilio] call error", e);
        setError(e.message || "Call error");
        setStatus("idle");
      });
      setCurrentCall(call);
      return call;
    } catch (e) {
      console.error("startCall failed", e);
      setError(e.message || "Call failed");
      setStatus("idle");
      return null;
    }
  }, [deviceReady]);

  const hangup = useCallback(() => {
    if (currentCall) currentCall.disconnect();
    else if (incomingCall) incomingCall.reject();
    else if (deviceRef.current) deviceRef.current.disconnectAll();
    setStatus("idle");
  }, [currentCall, incomingCall]);

  const toggleMute = useCallback(() => {
    if (!currentCall) return;
    const next = !muted;
    currentCall.mute(next);
    setMuted(next);
  }, [currentCall, muted]);

  const toggleHold = useCallback(() => {
    if (!currentCall) return;
    const next = !onHold;
    // "Hold" = mute microphone + set volume to 0 so the rep can neither
    // speak nor hear the other side. The Twilio leg stays connected so
    // billing continues but the remote party hears silence.
    try {
      currentCall.mute(next);
      // Volume 0 => rep does not hear the remote party while on hold
      if (typeof currentCall.getRemoteStream === "function") {
        const stream = currentCall.getRemoteStream();
        if (stream) stream.getAudioTracks().forEach((tr) => (tr.enabled = !next));
      }
    } catch (e) { console.warn("hold toggle failed", e); }
    setOnHold(next);
    // Keep mute state in sync — when releasing hold, unmute too
    setMuted(next);
  }, [currentCall, onHold]);

  const acceptIncoming = useCallback(() => {
    if (!incomingCall) return;
    incomingCall.accept();
    callMetaRef.current = {
      phone: incomingCall.parameters?.From || "",
      contactId: null,
      direction: "inbound",
      startedAt: Date.now(),
    };
    incomingCall.on("disconnect", () => {
      const meta = callMetaRef.current;
      const durationSec = meta.startedAt ? Math.round((Date.now() - meta.startedAt) / 1000) : 0;
      if (durationSec >= 3) {
        setLastCall({
          phone: meta.phone,
          contactId: meta.contactId,
          direction: meta.direction,
          durationSec,
          at: new Date().toISOString(),
        });
      }
      callMetaRef.current = {};
      setCurrentCall(null);
      setMuted(false);
      setOnHold(false);
      setStatus("idle");
    });
    setCurrentCall(incomingCall);
    setIncomingCall(null);
    setStatus("in-call");
  }, [incomingCall]);

  const rejectIncoming = useCallback(() => {
    if (!incomingCall) return;
    incomingCall.reject();
    setIncomingCall(null);
    setStatus("idle");
  }, [incomingCall]);

  return (
    <TwilioVoiceContext.Provider value={{
      enabled,
      deviceReady,
      currentCall,
      incomingCall,
      muted,
      onHold,
      status,
      error,
      fromNumber,
      lastCall,
      outOfMinutes,
      clearLastCall: () => setLastCall(null),
      startCall, hangup, toggleMute, toggleHold, acceptIncoming, rejectIncoming,
    }}>
      {children}
    </TwilioVoiceContext.Provider>
  );
}

export function useTwilioVoice() {
  const ctx = useContext(TwilioVoiceContext);
  if (!ctx) {
    // Provide safe defaults so non-rep users don't crash
    return {
      enabled: false, deviceReady: false, currentCall: null, incomingCall: null,
      muted: false, onHold: false, status: "idle", error: null, fromNumber: null,
      lastCall: null, outOfMinutes: false, clearLastCall: () => {},
      startCall: () => null, hangup: () => {}, toggleMute: () => {}, toggleHold: () => {},
      acceptIncoming: () => {}, rejectIncoming: () => {},
    };
  }
  return ctx;
}
